import React, { useEffect, useState, useMemo } from 'react';

import { FileSearchOutlined, LinkOutlined } from '@ant-design/icons';
import ListPageHeader from '@shared/ListPageHeader';
import { Button, Card, Descriptions, Tag, Spin, Alert, Table } from 'antd';
import { useTranslation } from 'react-i18next';
import { useParams, Link } from 'react-router-dom';

import { formatDateTime } from '../../../utils/dateTime';
import { resolveNotification, hasCustomRenderer, getNotifiablePath } from '../../../utils/notificationResolver';

/**
 * 通知詳情組件（Shared）
 *
 * @param {Function} service - API service 函數
 * @param {String} role - 角色標識（'admin' 或 'operator'）
 */
export default function NotificationShow({ service, role = 'admin' }) {
  const { t } = useTranslation();
  const { id } = useParams();

  const [notification, setNotification] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // 分類標籤顏色映射
  const getCategoryColor = (category) => {
    const colorMap = {
      system: 'blue',
      order: 'green',
      inbound: 'cyan',
      stock: 'orange',
      platform: 'magenta'
    };
    return colorMap[category] || 'default';
  };

  // 獲取通知詳情
  useEffect(() => {
    const fetchNotification = async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await service.get(id);
        if (result.success) {
          setNotification(result.data);
        } else {
          setError(result.message || t('common.error.operation_failed'));
        }
      } catch (err) {
        console.error('[NotificationShow] Failed to fetch notification:', err);
        setError(t('common.error.operation_failed'));
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchNotification();
    }
  }, [id, service, t]);

  // 翻譯後的 title / description
  const resolved = useMemo(() => {
    if (!notification) return { title: '', description: '' };
    return resolveNotification(notification);
  }, [notification]);

  // notifiable 跳轉路徑
  const notifiablePath = useMemo(() => {
    if (!notification) return null;
    return getNotifiablePath(notification, role);
  }, [notification, role]);

  const basePath = role === 'operator' ? '/operator' : '/admin';

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <ListPageHeader
          basePath={basePath}
          items={['notifications', { label: t('common.error.title') }]}
        />
        <Card>
          <Alert
            message={t('common.error.title')}
            description={error}
            type="error"
            showIcon
          />
        </Card>
      </div>
    );
  }

  if (!notification) {
    return (
      <div>
        <ListPageHeader
          basePath={basePath}
          items={['notifications', { label: t('common.error.record_not_found') }]}
        />
        <Card>
          <Alert
            message={t('common.error.record_not_found')}
            type="warning"
            showIcon
          />
        </Card>
      </div>
    );
  }

  // 自訂渲染：到期通知的 items 列表
  const renderExpirationItems = () => {
    const items = notification.content?.items;
    if (!items || items.length === 0) return null;

    const columns = [
      { title: 'SKU', dataIndex: 'sku', key: 'sku' },
      { title: t('common.name'), dataIndex: 'name', key: 'name' },
      { title: t('common.batch'), dataIndex: 'batch', key: 'batch' },
      { title: t('common.expiry_date'), dataIndex: 'expiry_date', key: 'expiry_date' },
      { title: t('common.days'), dataIndex: 'days', key: 'days' },
    ];

    return (
      <Table
        dataSource={items}
        columns={columns}
        rowKey="sku"
        size="small"
        pagination={false}
        style={{ marginTop: 8 }}
      />
    );
  };

  // 自訂渲染：缺少報關品名的商品列表
  const renderMissingCustomsProducts = () => {
    const products = notification.content?.products;
    if (!products || products.length === 0) return null;

    const columns = [
      { title: 'SKU', dataIndex: 'sku', key: 'sku' },
      {
        title: t('common.name'),
        dataIndex: 'name',
        key: 'name',
        render: (name, record) =>
          record.id ? (
            <Link to={`${basePath}/products/${record.id}`}>{name || '-'}</Link>
          ) : (
            name || '-'
          ),
      },
    ];

    return (
      <Table
        dataSource={products}
        columns={columns}
        rowKey="sku"
        size="small"
        pagination={false}
        style={{ marginTop: 8 }}
      />
    );
  };

  return (
    <div>
      <ListPageHeader
        basePath={basePath}
        items={['notifications', { label: resolved.title }]}
      />
      <Card>
        <Descriptions bordered column={1}>
          <Descriptions.Item label={t('notifications.show.fields.title')}>
            {resolved.title}
          </Descriptions.Item>

          <Descriptions.Item label={t('notifications.show.fields.content')}>
            {resolved.description && (
              <div style={{ whiteSpace: 'pre-wrap' }}>{resolved.description}</div>
            )}
            {notification.event_type === 'expiration_notification' && renderExpirationItems()}
            {notification.event_type === 'platform_missing_customs_declaration' && renderMissingCustomsProducts()}
            {!resolved.description &&
              notification.event_type !== 'expiration_notification' &&
              notification.event_type !== 'platform_missing_customs_declaration' &&
              '-'}
          </Descriptions.Item>

          <Descriptions.Item label={t('notifications.show.fields.category')}>
            <Tag color={getCategoryColor(notification.category)}>
              {t(`notifications.categories.${notification.category}`)}
            </Tag>
          </Descriptions.Item>

          <Descriptions.Item label={t('common.created_at')}>
            {formatDateTime(notification.created_at)}
          </Descriptions.Item>
        </Descriptions>

        {(notifiablePath || notification.content?.third_party_api_log_id) && (
          <div style={{ marginTop: '16px', display: 'flex', gap: '8px' }}>
            {notifiablePath && (
              <Link to={notifiablePath}>
                <Button icon={<LinkOutlined />} type="link">
                  {t('notifications.show.view_resource')}
                </Button>
              </Link>
            )}
            {notification.content?.third_party_api_log_id && (
              <Link to={`/third_party_api_logs/${notification.content.third_party_api_log_id}`}>
                <Button icon={<FileSearchOutlined />} type="link">
                  {t('notifications.show.view_api_log')}
                </Button>
              </Link>
            )}
          </div>
        )}
      </Card>
    </div>
  );
}
